import type { TeamCompetitorFormatSummary } from '../types/api.types';

export interface FormatMemberStats {
  memberId: string | number;
  memberName: string;
  played: number;
  won: number;
  lost: number;
  halved: number;
  totalPoints: number;
}

/**
 * Creates a format key from format and variation
 */
const getFormatKey = (format: string | null | undefined, variation: string | null | undefined): string => {
  return variation ? `${format} - ${variation}` : format || '';
};

/**
 * Creates a member name from first name and surname
 */
const getMemberName = (firstName: string | undefined, surname: string | undefined): string => {
  return `${firstName || ''} ${surname || ''}`;
};

/**
 * Calculates total points from wins and halves
 */
const calculateTotalPoints = (won: number, halved: number): number => {
  return won + halved / 2;
};

/**
 * Groups format summary by format/variation
 */
export const groupFormatsByVariation = (
  formatSummary: TeamCompetitorFormatSummary[]
): Map<string, Map<string | number, FormatMemberStats>> => {
  return formatSummary.reduce(
    (acc: Map<string, Map<string | number, FormatMemberStats>>, memberFormat: TeamCompetitorFormatSummary) => {
      const key = getFormatKey(memberFormat.format, memberFormat.variation);
      const formatVariation = acc.get(key) || new Map<string | number, FormatMemberStats>();

      const won = memberFormat.won ?? 0;
      const lost = memberFormat.lost ?? 0;
      const halved = memberFormat.halved ?? 0;

      formatVariation.set(memberFormat.memberID!, {
        memberId: memberFormat.memberID!,
        memberName: getMemberName(memberFormat.memberFirstName, memberFormat.memberSurname),
        played: won + lost + halved,
        won,
        lost,
        halved,
        totalPoints: calculateTotalPoints(won, halved),
      });

      acc.set(key, formatVariation);
      return acc;
    },
    new Map<string, Map<string | number, FormatMemberStats>>()
  );
};

/**
 * Aggregates all formats into a single summary
 */
export const aggregateAllFormats = (
  formatSummary: TeamCompetitorFormatSummary[]
): Map<string | number, FormatMemberStats> => {
  return formatSummary.reduce(
    (acc: Map<string | number, FormatMemberStats>, memberFormat: TeamCompetitorFormatSummary) => {
      const memberAgg = acc.get(memberFormat.memberID!) || {
        memberId: memberFormat.memberID!,
        memberName: getMemberName(memberFormat.memberFirstName, memberFormat.memberSurname),
        played: 0,
        won: 0,
        lost: 0,
        halved: 0,
        totalPoints: 0,
      };

      const won = memberFormat.won ?? 0;
      const lost = memberFormat.lost ?? 0;
      const halved = memberFormat.halved ?? 0;

      acc.set(memberFormat.memberID!, {
        memberId: memberFormat.memberID!,
        memberName: getMemberName(memberFormat.memberFirstName, memberFormat.memberSurname),
        played: memberAgg.played + won + lost + halved,
        won: memberAgg.won + won,
        lost: memberAgg.lost + lost,
        halved: memberAgg.halved + halved,
        totalPoints: memberAgg.totalPoints + calculateTotalPoints(won, halved),
      });

      return acc;
    },
    new Map<string | number, FormatMemberStats>()
  );
};

/**
 * Transforms format summary data into grouped and aggregated statistics
 */
export const transformFormatSummary = (
  formatSummary: TeamCompetitorFormatSummary[]
): Map<string, Map<string | number, FormatMemberStats>> => {
  const groupedFormat = groupFormatsByVariation(formatSummary);
  const allFormats = aggregateAllFormats(formatSummary);

  groupedFormat.set('All', allFormats);

  return groupedFormat;
};

/**
 * Format summary for sorting by popularity
 */
export interface FormatSummary {
  formatName: string;
  totalPlayed: number;
}

/**
 * Summarizes formats by total plays and sorts by popularity
 *
 * @param mapFormatSummary - Map of format names to member stats
 * @returns Array of format summaries sorted by total played (descending)
 */
export const summarizeFormatsByPopularity = (
  mapFormatSummary: Map<string, Map<string | number, FormatMemberStats>>
): FormatSummary[] => {
  const formatsPlayed = [...mapFormatSummary].map((format) => ({
    formatName: format[0],
    totalPlayed: [...format[1]].reduce((acc, currFormat) => acc + parseInt(String(currFormat[1].played), 10), 0),
  }));

  return formatsPlayed.sort((a, b) => b.totalPlayed - a.totalPlayed);
};

/**
 * Sorts format member results by performance
 * Higher points, more wins, more halves, and more played games are ranked higher
 *
 * @param a - First member entry
 * @param b - Second member entry
 * @returns Comparison result for sort
 */
export const sortFormatMemberResults = (
  a: [string | number, FormatMemberStats],
  b: [string | number, FormatMemberStats]
): number => {
  if (b[1].totalPoints === a[1].totalPoints) {
    if (b[1].won === a[1].won) {
      if (b[1].halved === a[1].halved) {
        return b[1].played - a[1].played;
      }
      return b[1].halved - a[1].halved;
    }
    return b[1].won - a[1].won;
  }
  return b[1].totalPoints - a[1].totalPoints;
};

/**
 * Calculate win percentage from format member stats
 *
 * @param stats - Format member statistics
 * @returns Win percentage as a formatted string (e.g., "75%")
 */
export const calculateWinPercentage = (stats: FormatMemberStats): string => {
  const totalGames = stats.won + stats.halved + stats.lost;
  if (totalGames === 0) return '0%';

  const percentage = (stats.totalPoints / totalGames) * 100;
  return `${percentage.toFixed(0)}%`;
};
